import AuthenticatedStatusService from 'frontcore/domain/authorization/model/AuthenticatedStatusService'
import AuthorizationLoginService from 'frontcore/domain/authorization/model/AuthorizationLoginService'
import AuthorizationSSOLoginService from 'frontcore/domain/authorization/model/AuthorizationSSOLoginService'
import PermissionCredentialsServiceImpl from 'frontcore/domain/authorization/model/PermissionCredentialsService'
import PermissionSSOServiceImpl from 'frontcore/domain/authorization/model/PermissionSSOService'
import usePasswordUpdate from './model/usePasswordUpdate'
import usePasswordValidation, { PasswordValidationUseCase } from 'frontcore/domain/authorization/model/usePasswordValidation'
import usePermissionSAML, { PermissionSAMLUseCase } from 'frontcore/domain/authorization/view/usePermissionSAML'
import useSAMLLogin, { AuthorizationSAMLUseCase } from 'frontcore/domain/authorization/view/useSAMLLogin'
import {
  AuthorizationAPIClientFactory,
  AuthorizationSAMLAPIClientFactory,
  AuthorizationSSOAPIClientFactory,
  PasswordUpdateAPIClientFactory,
} from 'frontcore/domain/authorization/data/factory'
import {
  AuthorizationCredentialsService,
  AuthorizationSSOService,
  AuthorizationSSOView,
  AuthorizationStatusService,
} from 'frontcore/domain/authorization/model/AuthorizationService.d'
import { AuthorizationSSOConfiguration } from 'frontcore/domain/authorization/model/types'
import {
  AuthorizationSSORedirectStorageFactory,
  AuthorizationStorageFactory,
  PermissionSSORedirectStorageFactory,
  PermissionStorageFactory,
} from 'frontcore/domain/authorization/store/factory'
import { PasswordUpdateUseCase } from 'frontcore/domain/authorization/model/usePasswordUpdate.d'
import {
  PermissionCredentialsService,
  PermissionSSOService,
  PermissionSSOView,
} from 'frontcore/domain/authorization/model/PermissionService.d'
import { ProfileRequesterFactory } from 'frontcore/domain/profile/factory'
import { Ref } from 'vue'

export class AuthorizationCredentialsServiceFactory {
  static build (): AuthorizationCredentialsService {
    return new AuthorizationLoginService(
      AuthorizationStorageFactory.build(),
      AuthorizationAPIClientFactory.build()
    )
  }
}

export class AuthorizationSSOServiceFactory {
  static build (
    configuration: AuthorizationSSOConfiguration,
    view: AuthorizationSSOView
  ): AuthorizationSSOService {
    return new AuthorizationSSOLoginService(
      AuthorizationAPIClientFactory.build(),
      AuthorizationSSOAPIClientFactory.build(configuration),
      AuthorizationSSORedirectStorageFactory.build(),
      AuthorizationStorageFactory.build(),
      view
    )
  }
}

export class PermissionCredentialsServiceFactory {
  static build (): PermissionCredentialsService {
    return new PermissionCredentialsServiceImpl(
      AuthorizationAPIClientFactory.build(),
      ProfileRequesterFactory.build()
    )
  }
}

export class PermissionSSOServiceFactory {
  static build (
    configuration: AuthorizationSSOConfiguration,
    view: PermissionSSOView
  ): PermissionSSOService {
    return new PermissionSSOServiceImpl(
      AuthorizationAPIClientFactory.build(),
      PermissionSSORedirectStorageFactory.build(),
      PermissionStorageFactory.build(),
      AuthorizationSSOAPIClientFactory.build(configuration),
      ProfileRequesterFactory.build(),
      view
    )
  }
}

export class AuthorizationStatusFactory {
  static build (): AuthorizationStatusService {
    return new AuthenticatedStatusService(
      AuthorizationStorageFactory.build()
    )
  }
}

export class PasswordValidationUseCaseFactory {
  static build (password: Ref<string>): PasswordValidationUseCase {
    return usePasswordValidation(password)
  }
}

export class PasswordUpdateUseCaseFactory {
  static build (): PasswordUpdateUseCase {
    return usePasswordUpdate(
      AuthorizationStorageFactory.build(),
      PasswordUpdateAPIClientFactory.build()
    )
  }
}

export class AuthorizationSAMLUseCaseFactory {
  static build (): AuthorizationSAMLUseCase {
    return useSAMLLogin(
      AuthorizationStorageFactory.build(),
      AuthorizationSAMLAPIClientFactory.build())
  }
}

export class PermissionSAMLUseCaseFactory {
  static build (loginType?: number): PermissionSAMLUseCase {
    return usePermissionSAML(
      PermissionStorageFactory.build(),
      AuthorizationSAMLAPIClientFactory.build(),
      ProfileRequesterFactory.build(),
      loginType
    )
  }
}
